<template>
  <div class="card mb-4">
    <div class="card-header">
      <h5 class="mb-0">Upload Power BI Document</h5>
    </div>
    <div class="card-body">
      <p class="text-muted mb-3">
        Upload a Power BI CSV or Excel file to import monthly production data
        for LT ESO devices. The system will automatically match object numbers
        to registered devices.
      </p>

      <!-- How to get the data from ESO (collapsible, collapsed by default) -->
      <div class="mb-3">
        <button
          class="btn btn-link btn-sm p-0 text-decoration-none"
          type="button"
          @click="showEsoHint = !showEsoHint"
          :aria-expanded="showEsoHint ? 'true' : 'false'"
        >
          <font-awesome-icon
            :icon="showEsoHint ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
            class="me-2"
          />
          How to get the data from ESO
        </button>
        <div v-show="showEsoHint" class="alert alert-info mt-2 mb-0">
          <ol class="mb-0">
            <li class="mb-1">
              Open the
              <a
                href="https://app.powerbi.com/groups/me/reports/e88d1678-f7c6-44df-a77b-cbece542c255/dd9b5691722a129c102f?ctid=ea88e983-d65a-47b3-adb4-3e1c6d2110d2&amp;experience=power-bi"
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary"
              >
                ESO Power BI report </a
              >.
            </li>
            <li class="mb-1">
              Log in with <strong>stenver@soldera.org</strong> (password in
              Bitwarden under
              <strong>&ldquo;ESO API KEY + datahub&rdquo;</strong>).
            </li>
            <li class="mb-1">
              Apply a filter in ESO: meter readings for the
              <strong>last two months</strong> only.
            </li>
            <li>Export the report and upload the file below.</li>
          </ol>
        </div>
      </div>

      <!-- File Upload Zone -->
      <div v-if="!uploadResult">
        <div
          class="dropzone dz-clickable d-flex justify-content-center align-items-center px-4 mb-3"
          @dragover.prevent="dragOver"
          @dragleave.prevent="dragLeave"
          @drop.prevent="handleDrop"
          @click="triggerFileInput"
          :class="{ dragging: isDragging, 'bg-light': isUploading }"
        >
          <input
            class="form-control visually-hidden"
            type="file"
            @change="handleFileUpload"
            ref="fileInput"
            accept=".csv,.xlsx"
          />
          <div class="dropzone-content text-center">
            <div v-if="!isUploading">
              <div class="upload-icon mb-2">
                <font-awesome-icon icon="fas fa-cloud-upload-alt" size="2x" />
              </div>
              <p class="mb-1 fw-medium">
                Drag and drop your file here or click to browse
              </p>
              <small class="text-muted"
                >Accepts .csv and .xlsx files (max 25MB)</small
              >
            </div>
            <LoadingIndicatorComponent
              v-else
              message="Uploading and processing file..."
            />
          </div>
        </div>
      </div>

      <!-- Upload Another Button -->
      <div v-else class="mb-4">
        <button class="btn btn-outline-primary btn-sm" @click="resetUpload">
          <font-awesome-icon icon="fas fa-sync" class="me-1" />
          Upload another file
        </button>
      </div>

      <!-- Summary Statistics -->
      <div v-if="uploadResult" class="card bg-light mb-3">
        <div class="card-body">
          <h6 class="card-title mb-3">Upload Summary</h6>
          <div class="d-flex gap-3 flex-wrap">
            <div>
              <span class="badge bg-success">
                Created: {{ uploadResult.total_records_created }}
              </span>
            </div>
            <div>
              <span class="badge bg-primary">
                Updated: {{ uploadResult.total_records_updated }}
              </span>
            </div>
            <div>
              <span class="badge bg-secondary">
                Skipped: {{ uploadResult.total_records_skipped }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Warnings Section -->
      <div
        v-if="uploadResult && (hasUnmappedColumns || hasAmbiguousColumns)"
        class="alert alert-warning mb-3"
      >
        <div class="d-flex align-items-start">
          <font-awesome-icon
            icon="fas fa-exclamation-triangle"
            class="me-2 mt-1"
          />
          <div class="flex-grow-1">
            <h6 class="alert-heading mb-2">Warnings</h6>

            <div v-if="hasUnmappedColumns" class="mb-2">
              <strong>Unmapped Columns:</strong>
              <p class="mb-1 small">
                These object numbers could not be matched to any active devices
                in the system. No records were created for these columns.
              </p>
              <div class="d-flex gap-2 flex-wrap">
                <span
                  v-for="column in uploadResult.unmapped_columns"
                  :key="column"
                  class="badge bg-warning text-dark"
                >
                  {{ column }}
                </span>
              </div>
            </div>

            <div v-if="hasAmbiguousColumns">
              <strong>Ambiguous Columns:</strong>
              <p class="mb-1 small">
                These object numbers matched multiple devices in the system. No
                records were created for these columns to avoid data conflicts.
              </p>
              <div class="d-flex gap-2 flex-wrap">
                <span
                  v-for="column in uploadResult.ambiguous_columns"
                  :key="column"
                  class="badge bg-warning text-dark"
                >
                  {{ column }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Per-Company Accordion -->
      <div v-if="uploadResult && uploadResult.company_results.length > 0">
        <h6 class="mb-3">Results by Company</h6>
        <div class="accordion" id="companyResultsAccordion">
          <div
            v-for="(companyResult, index) in uploadResult.company_results"
            :key="companyResult.company_id"
            class="accordion-item"
          >
            <h2 class="accordion-header" :id="`heading-${index}`">
              <button
                class="accordion-button"
                :class="{ collapsed: openAccordionIndex !== index }"
                type="button"
                @click="toggleAccordion(index)"
                :aria-expanded="openAccordionIndex === index ? 'true' : 'false'"
                :aria-controls="`collapse-${index}`"
              >
                <div class="d-flex justify-content-between w-100 pe-3">
                  <span class="fw-semibold">{{
                    companyResult.company_name ||
                    `Company ${companyResult.company_id}`
                  }}</span>
                  <span class="text-muted small">
                    <span class="badge bg-success me-1">{{
                      companyResult.total_created
                    }}</span>
                    <span class="badge bg-primary me-1">{{
                      companyResult.total_updated
                    }}</span>
                    <span class="badge bg-secondary">{{
                      companyResult.total_skipped
                    }}</span>
                  </span>
                </div>
              </button>
            </h2>
            <div
              :id="`collapse-${index}`"
              class="accordion-collapse collapse"
              :class="{ show: openAccordionIndex === index }"
              :aria-labelledby="`heading-${index}`"
            >
              <div class="accordion-body">
                <div class="table-responsive">
                  <table class="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Device Name</th>
                        <th>GSRN</th>
                        <th>ESO Object Number</th>
                        <th>Value (MWh)</th>
                        <th>Period</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="device in companyResult.device_results"
                        :key="`${device.registry_device_id}-${device.period_start_at}`"
                      >
                        <td>{{ device.device_name || "-" }}</td>
                        <td>
                          <small>{{ device.device_gsrn || "-" }}</small>
                        </td>
                        <td>{{ device.eso_object_number || "-" }}</td>
                        <td>{{ formatMwh(device.value_mwh) }}</td>
                        <td>{{ formatPeriod(device.period_start_at) }}</td>
                        <td>
                          <span
                            class="badge"
                            :class="getStatusBadgeClass(device.status)"
                          >
                            {{ capitalize(device.status) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Message -->
      <div
        v-if="uploadResult && uploadResult.company_results.length === 0"
        class="alert alert-info"
      >
        <font-awesome-icon icon="fas fa-info-circle" class="me-2" />
        No records were created. This may be because all columns were unmapped
        or ambiguous, or all records were already completed.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { capitalize } from "lodash-es";
import { computed, inject, ref, useTemplateRef } from "vue";
import { uploadPowerBiFile } from "./api";
import type { LtPowerBiBulkUploadResponse } from "./types";
import { formatMwh, formatPeriod, getStatusBadgeClass } from "./utils";

// Global error handler injection
const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

// Emit events
const emit = defineEmits<{
  "upload:success": [result: LtPowerBiBulkUploadResponse];
}>();

// State
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const isDragging = ref(false);
const isUploading = ref(false);
const uploadResult = ref<LtPowerBiBulkUploadResponse | null>(null);
const openAccordionIndex = ref<number>(-1);
const showEsoHint = ref(false);

// Computed properties
const hasUnmappedColumns = computed(
  () => uploadResult.value && uploadResult.value.unmapped_columns.length > 0
);

const hasAmbiguousColumns = computed(
  () => uploadResult.value && uploadResult.value.ambiguous_columns.length > 0
);

// Methods
function dragOver() {
  isDragging.value = true;
}

function dragLeave() {
  isDragging.value = false;
}

function handleDrop(event: DragEvent) {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handleFile(files[0]);
  }
}

function triggerFileInput() {
  if (!isUploading.value) {
    fileInput.value?.click();
  }
}

function handleFileUpload(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    handleFile(files[0]);
  }
}

async function handleFile(file: File) {
  if (!validateFile(file)) {
    return;
  }

  isUploading.value = true;
  uploadResult.value = null;

  try {
    const result = await uploadPowerBiFile(file);
    uploadResult.value = result;
    emit("upload:success", result);
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    isUploading.value = false;

    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = "";
    }
  }
}

function validateFile(file: File): boolean {
  const allowedTypes = /\.(csv|xlsx)$/i;
  const maxSizeMb = 25;

  if (!allowedTypes.test(file.name)) {
    globalErrorHandler(
      new Error("Invalid file type. Please upload a CSV or Excel (.xlsx) file.")
    );
    return false;
  }

  if (file.size / 1_048_576 > maxSizeMb) {
    globalErrorHandler(
      new Error(
        `File size exceeds ${maxSizeMb}MB limit. Please upload a smaller file.`
      )
    );
    return false;
  }

  return true;
}

function resetUpload() {
  uploadResult.value = null;
  openAccordionIndex.value = -1;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

function toggleAccordion(index: number) {
  // Toggle: if clicking the open accordion, close it; otherwise open the clicked one
  openAccordionIndex.value = openAccordionIndex.value === index ? -1 : index;
}
</script>

<style scoped lang="scss">
.dropzone {
  border: 3px dashed var(--bs-secondary);
  border-radius: 10px;
  min-height: 150px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--bs-body-color);
  transition: background-color 0.2s ease-out;

  &.dragging {
    background-color: var(--bs-secondary-bg-subtle);
  }

  &:hover:not(.bg-light) {
    background-color: var(--phoenix-primary-bg-subtle);
    color: var(--phoenix-primary);
    transition: background-color 0.2s ease-out;
  }

  .upload-icon {
    color: var(--bs-secondary);
  }
}

.card {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
}

.card-header {
  background-color: transparent;
  border-bottom: 1px solid var(--phoenix-gray-200);
  padding: 1rem;
}

.accordion-button:not(.collapsed) {
  background-color: var(--bs-light);
  color: var(--bs-body-color);
}

.table {
  font-size: 0.9rem;
}

.table th {
  background-color: var(--bs-light);
  font-weight: 600;
}
</style>
